// Session policy: which token to send, and what a 401 means.
//
// Anonymous continuity is the stored token, nothing else: it is bound to one
// conversation, slides for an hour at a time and dies for good after seven
// days. So `session_expired` is refreshed and retried once, while
// `session_invalid` / `session_not_bound` mean the state is dead and the
// next message starts a fresh conversation.

import { isApiError, type Api } from './api'
import {
  clearConversationId,
  clearSession,
  clearVisitor,
  readConversationId,
  readSession,
  readVisitor,
  writeConversationId,
  writeSession,
  writeVisitor,
} from './storage'
import type { SessionGrant, Visitor } from './types'

// IdentityError: the customer-signed JWT was rejected. The host has to
// mint a fresh one; nothing the widget does on its own can fix it.
export class IdentityError extends Error {
  constructor() { super('widget: identity token rejected') }
}

/** How close to the sliding expiry is worth refreshing early. */
const REFRESH_AHEAD_MS = 5 * 60_000

export type Outcome<T> =
  | { ok: true; value: T }
  | { ok: false; reason: 'no-session' | 'unauthorized' }

export interface SessionManager {
  /**
   * Runs an authenticated call. A 401 with a verified JWT on hand re-mints
   * once and retries; a final 401 clears the stored session and
   * conversation (unless `keepOnUnauthorized`, for routes like history
   * where 401 only means "not verified") and reports `unauthorized`.
   */
  authed<T>(fn: (token: string) => Promise<T>, opts?: { keepOnUnauthorized?: boolean }): Promise<Outcome<T>>
  /** Creates a conversation with its first message and stores the grant. */
  bootstrap(content: string, sourceUrl?: string): Promise<{ conversationId: string; verified: boolean }>
  /**
   * Mints a verified session for `jwt`, or clears everything for `null`.
   * Reports whether the stored conversation had to be left behind: an
   * anonymous thread belongs to an anonymous contact, so signing in cannot
   * carry it over.
   */
  rebind(jwt: string | null): Promise<{ droppedConversation: boolean }>
  /** Extends the stored session if the API still knows it. */
  refresh(): Promise<boolean>
  /** Extends it ahead of time when the sliding window is nearly up. */
  refreshIfStale(): Promise<void>
  isVerified(): boolean
  /**
   * True when the project asks everyone to say who they are and this
   * visitor hasn't — no token from the page, no verified session, and
   * nothing typed into the pre-chat form yet.
   */
  needsVisitor(): boolean
  /** Remembers what the pre-chat form collected. */
  setVisitor(visitor: Visitor): void
}

export function createSessionManager(
  api: Api,
  opts: {
    clientId: string
    getJwt: () => string | undefined
    /** The project's mode, read late: the config arrives after boot. */
    asksVisitor?: () => boolean
  },
): SessionManager {
  function store(grant: SessionGrant) {
    writeSession({
      token: grant.sessionToken,
      expiresAt: grant.expiresAt,
      absoluteExpiresAt: grant.absoluteExpiresAt,
      verified: grant.verified,
    })
  }

  // Binds the new session to the stored conversation when the visitor owns
  // it; a conversation the API does not recognise is dropped, not fatal.
  async function mint(jwt: string): Promise<{ droppedConversation: boolean }> {
    const conversationId = readConversationId() ?? undefined
    try {
      store(await api.mintSession(opts.clientId, jwt, conversationId))
      return { droppedConversation: false }
    } catch (err) {
      if (isApiError(err, 404, 'conversation_not_found') && conversationId) {
        clearConversationId()
        store(await api.mintSession(opts.clientId, jwt))
        return { droppedConversation: true }
      }
      if (isApiError(err) && (err.code === 'identity_invalid' || err.status === 400 || err.status === 401)) {
        throw new IdentityError()
      }
      throw err
    }
  }

  // An idle session past its sliding expiry can be revived until its
  // absolute lifetime ends. Resolves false when it cannot.
  async function refresh(token: string): Promise<boolean> {
    const current = readSession()
    if (!current || current.token !== token) return false
    try {
      const next = await api.refreshSession(token)
      writeSession({ ...current, expiresAt: next.expiresAt, absoluteExpiresAt: next.absoluteExpiresAt })
      return true
    } catch {
      return false
    }
  }

  async function authed<T>(
    fn: (token: string) => Promise<T>,
    o: { keepOnUnauthorized?: boolean } = {},
  ): Promise<Outcome<T>> {
    let session = readSession()
    let canMint = !!opts.getJwt()
    if (!session) {
      if (!canMint) return { ok: false, reason: 'no-session' }
      try {
        await mint(opts.getJwt()!)
      } catch {
        return { ok: false, reason: 'no-session' }
      }
      canMint = false
      session = readSession()
      if (!session) return { ok: false, reason: 'no-session' }
    }
    let canRefresh = true
    for (;;) {
      try {
        return { ok: true, value: await fn(session.token) }
      } catch (err) {
        // Only history answers this, and only for anonymous visitors: the
        // session is fine, it just isn't allowed there.
        if (isApiError(err, 403, 'verified_session_required')) {
          return { ok: false, reason: 'unauthorized' }
        }
        if (!isApiError(err, 401)) throw err
        // Idle past the sliding window; the token itself is still good.
        if (canRefresh && err.code === 'session_expired') {
          canRefresh = false
          if (await refresh(session.token)) continue
        }
        if (canMint) {
          canMint = false
          try {
            await mint(opts.getJwt()!)
            const next = readSession()
            if (next) { session = next; continue }
          } catch {
            // Re-mint failed; fall through to the unauthorized outcome.
          }
        }
        if (!o.keepOnUnauthorized) {
          clearSession()
          clearConversationId()
        }
        return { ok: false, reason: 'unauthorized' }
      }
    }
  }

  async function bootstrap(content: string, sourceUrl?: string) {
    const jwt = opts.getJwt()
    // The token wins when both are on hand; the form's answer is the
    // fallback the API accepts in `ask` mode.
    const visitor = jwt ? undefined : (readVisitor() ?? undefined)
    let out
    try {
      out = await api.createConversation({
        clientId: opts.clientId,
        content,
        sourceUrl,
        userJwt: jwt,
        visitor,
      })
    } catch (err) {
      if (jwt && isApiError(err) && (err.code === 'identity_invalid' || err.status === 400)) {
        throw new IdentityError()
      }
      throw err
    }
    store(out)
    writeConversationId(out.conversationId)
    return { conversationId: out.conversationId, verified: out.verified }
  }

  async function rebind(jwt: string | null) {
    if (!jwt) {
      clearSession()
      clearConversationId()
      clearVisitor()
      return { droppedConversation: false }
    }
    return mint(jwt)
  }

  return {
    authed,
    bootstrap,
    rebind,
    refresh: async () => {
      const current = readSession()
      return current ? refresh(current.token) : false
    },
    // Saves the visitor a visible round-trip on their first message back.
    refreshIfStale: async () => {
      const current = readSession()
      if (!current) return
      const left = new Date(current.expiresAt).getTime() - Date.now()
      if (left < REFRESH_AHEAD_MS) await refresh(current.token)
    },
    isVerified: () => !!readSession()?.verified,
    needsVisitor: () =>
      !!opts.asksVisitor?.() && !opts.getJwt() && !readSession()?.verified && !readVisitor(),
    setVisitor: (visitor) => writeVisitor(visitor),
  }
}
